import { Form, Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import { Button, Grid, Header, SemanticSIZES } from 'semantic-ui-react';
import { PermitFormValues, VehicleFormValues } from '../../../../app/models/permit';
import { useStore } from '../../../../app/stores/store';
import { transitAgency } from '../../../../app/common/options/transitAgencyOptions';
import HOVMessage from '../../../../app/common/messages/HOVMessage';
import * as Yup from 'yup';
import { VanpoolVehicleFormValidationSchema, VehicleFormValidationSchema } 
    from '../../../../app/common/form/FormikFormValidations';
import MyTextInput from '../../../../app/common/form/MyTextInput';
import MyYearTextInput from '../../../../app/common/form/MyYearTextInput';
import MySelectInput from '../../../../app/common/form/MySelectInput';

interface Props {
    propValues: PermitFormValues,
    vehicle: VehicleFormValues,
    handleChange: (name: string|any, value: string|object) => void,
    buttonSize: SemanticSIZES
}

export default observer(function PermitFormStep4VehicleEditForm({ 
    propValues,
    vehicle,
    handleChange,
    buttonSize
 }: Props) {
    const {modalStore: {closeModal}} = useStore();

    function handleFinalFormSubmit(formValues: VehicleFormValues) {
        let vehicleObj = propValues.vehicles.find(x => x.id === vehicle.id)!;
          
        vehicleObj.make = formValues.make.trim();
        vehicleObj.model = formValues.model.trim();
        vehicleObj.year = +formValues.year; // input converted to string so needs to cast to number
        vehicleObj.licensePlateNbr = formValues.licensePlateNbr.trim();
        vehicleObj.vehicleIsPrimary = formValues.vehicleIsPrimary;
        vehicleObj.hovTransitVehicleNbr = formValues.hovTransitVehicleNbr.trim();
        vehicleObj.transitAgency = formValues.transitAgency.trim();

        // this cause of observable values without using an action warnings
        handleChange('vehicles', propValues.vehicles);
        closeModal();
    }

    return (
        <Formik
            initialValues={vehicle}
            onSubmit={(values) => handleFinalFormSubmit(values)}
            validationSchema={() => Yup.lazy((values) => {
                if (propValues.permitTypeId === 2) {
                    return VehicleFormValidationSchema;
                } else {
                    return VanpoolVehicleFormValidationSchema;
                }
            })}
        >
        {({handleSubmit, isSubmitting, dirty, values, isValid}) => (
            <Form className='ui form error' onSubmit={handleSubmit} autoComplete='off'>
                <Grid>
                    <Grid.Column width={16}>
                        <Header 
                            as='h2' 
                            icon='car' 
                            content='Update Vehicle Information'
                        />
                        
                        <HOVMessage permitTypeId={propValues.permitTypeId} />

                        <label
                            className={values.make ? 'label-visible input-label' : 'input-label'}
                        >Make</label>
                        <MyTextInput name='make' placeholder='Make' tabIndex={1} />

                        <label 
                            className={values.model ? 'label-visible input-label' : 'input-label'}
                        >Model</label>
                        <MyTextInput name='model' placeholder='Model' tabIndex={2} />

                        <label
                            className={values.year ? 'label-visible input-label' : 'input-label'}
                        >Year</label>
                        <MyYearTextInput name='year' placeholder='Year' tabIndex={3} />

                        <label
                            className={values.licensePlateNbr ? 'label-visible input-label' : 'input-label'}
                        >License Plate</label>
                        <MyTextInput name='licensePlateNbr' placeholder='License Plate' tabIndex={4} />

                        {propValues.permitTypeId === 3 && 
                        <>
                            <label
                                className={values.hovTransitVehicleNbr ? 'label-visible input-label' : 'input-label'}
                            >HOV Number*</label>
                            <MyTextInput name='hovTransitVehicleNbr' placeholder='HOV Number*' tabIndex={5} />

                            <label className={'label-visible input-label'}>Transit Agency</label>
                            <MySelectInput 
                                options={transitAgency} 
                                placeholder='--- Select Transit Agency ---'  
                                name='transitAgency' 
                                tabIndex={6} 
                            />
                        </>
                        }
                    </Grid.Column>
                    <Grid.Column width={16}>
                        <Button 
                            disabled={!dirty || !isValid} 
                            loading={isSubmitting} 
                            floated='right'
                            positive
                            type='submit'
                            content={'Edit Vehicle'}
                            size={buttonSize}
                        /> 
                    </Grid.Column>
                </Grid>
            </Form>
        )}
        </Formik>
    )
})